import * as XLSX from "xlsx";
import * as pdfjsLib from "pdfjs-dist";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * Convert various file types to plain text
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - The extracted text content
 */
export const convertFileToText = async (file) => {
  const fileType = file.type;
  const fileName = file.name.toLowerCase();

  try {
    // Handle XLSX/XLS files
    if (
      fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      fileType === "application/vnd.ms-excel" ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".xls")
    ) {
      return await convertExcelToText(file);
    }

    // Handle PDF files
    if (fileType === "application/pdf" || fileName.endsWith(".pdf")) {
      return await convertPdfToText(file);
    }

    // Handle TXT files
    if (fileType === "text/plain" || fileName.endsWith(".txt")) {
      return await convertTextFile(file);
    }

    // Handle CSV files (already text, just read)
    if (fileType === "text/csv" || fileName.endsWith(".csv")) {
      return await convertTextFile(file);
    }

    // Handle ODT files
    if (
      fileType === "application/vnd.oasis.opendocument.text" ||
      fileType === "application/vnd.oasis.opendocument.spreadsheet" ||
      fileName.endsWith(".odt") ||
      fileName.endsWith(".ods")
    ) {
      return await convertOdtToText(file);
    }

    // Handle DOCX files (Word documents)
    if (
      fileType ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      fileName.endsWith(".docx")
    ) {
      return await convertDocxToText(file);
    }

    throw new Error(`Unsupported file type: ${fileType || fileName}`);
  } catch (error) {
    console.error("File conversion error:", error);
    throw new Error(`Failed to convert file: ${error.message}`);
  }
};

/**
 * Convert Excel file to text
 */
const convertExcelToText = async (file) => {
  console.log("[FileTransformer] Starting Excel conversion for:", file.name);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        console.log("[FileTransformer] File read complete, parsing XLSX...");
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        console.log("[FileTransformer] Workbook parsed. Sheets:", workbook.SheetNames);

        let textContent = "";

        // Iterate through all sheets
        workbook.SheetNames.forEach((sheetName, index) => {
          const worksheet = workbook.Sheets[sheetName];

          // Add sheet name as header if multiple sheets
          if (workbook.SheetNames.length > 1) {
            textContent += `\n=== Sheet: ${sheetName} ===\n\n`;
          }

          // Convert sheet to CSV format
          const csv = XLSX.utils.sheet_to_csv(worksheet);
          console.log(
            `[FileTransformer] Sheet ${sheetName} converted to CSV. Length: ${csv.length}`
          );
          textContent += csv + "\n";
        });

        console.log(
          "[FileTransformer] Excel conversion complete. Total length:",
          textContent.length
        );
        resolve(textContent.trim());
      } catch (error) {
        console.error("[FileTransformer] Excel parsing error:", error);
        reject(new Error(`Excel parsing error: ${error.message}`));
      }
    };

    reader.onerror = () => {
      console.error("[FileTransformer] FileReader error");
      reject(new Error("Failed to read Excel file"));
    };
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Convert PDF file to text
 */
const convertPdfToText = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const typedArray = new Uint8Array(e.target.result);
        const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;

        let textContent = "";

        // Extract text from each page
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const content = await page.getTextContent();

          const pageText = content.items.map((item) => item.str).join(" ");
          textContent += `${pageText}\n`;
        }

        resolve(textContent.trim());
      } catch (error) {
        reject(new Error(`PDF parsing error: ${error.message}`));
      }
    };

    reader.onerror = () => reject(new Error("Failed to read PDF file"));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Convert plain text file to text
 */
const convertTextFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      resolve(e.target.result);
    };

    reader.onerror = () => reject(new Error("Failed to read text file"));
    reader.readAsText(file);
  });
};

/**
 * Convert DOCX file to text (basic extraction)
 * Note: This is a basic implementation. For better results, consider using mammoth.js
 */
const convertDocxToText = async (file) => {
  // For DOCX, we'll extract what we can using a simple approach
  // In a production app, you might want to use mammoth.js library
  // For now, we'll return a message indicating DOCX is not fully supported
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        // DOCX files are zip archives containing XML files
        // For basic extraction, we'll try to extract readable text
        // This is a simplistic approach - for better results use mammoth.js
        const text = extractTextFromDocx(e.target.result);
        resolve(text);
      } catch (error) {
        reject(
          new Error(
            `DOCX parsing error: ${error.message}. Consider converting to PDF or TXT first.`
          )
        );
      }
    };

    reader.onerror = () => reject(new Error("Failed to read DOCX file"));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Basic DOCX text extraction
 */
const extractTextFromDocx = (arrayBuffer) => {
  // This is a placeholder for DOCX extraction
  // A proper implementation would use JSZip and parse the XML content
  // For now, we'll return a message
  return "DOCX file uploaded. Note: For best results with Word documents, please convert to PDF or TXT format first, or paste the text content directly into the chat.";
};

/**
 * Convert ODT file to text (basic extraction)
 * Note: ODT is a complex format. This is a basic implementation.
 */
const convertOdtToText = async (file) => {
  // ODT files are also zip archives with XML content
  // For production, you might want to use a dedicated library
  return new Promise((resolve) => {
    // For now, provide a helpful message
    resolve(
      "ODT file uploaded. Note: For best results with ODT documents, please convert to PDF, TXT, or XLSX format first, or paste the content directly into the chat."
    );
  });
};
